import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Wrench, CheckCircle, Upload, X, Image, Video, Mic, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 30 }, (_, i) => (currentYear - i).toString());

const AssistenzaPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    product_name: "",
    product_brand: "",
    purchase_year: "",
    issue_description: ""
  });
  
  const [mediaFiles, setMediaFiles] = useState([]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedFiles = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        
        const response = await axios.post(`${API}/upload/media`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        
        uploadedFiles.push({
          url: `${BACKEND_URL}${response.data.url}`,
          type: response.data.type,
          filename: response.data.filename
        });
      } catch (error) {
        toast.error(`Errore nel caricamento di ${file.name}`);
      }
    }

    setMediaFiles(prev => [...prev, ...uploadedFiles]);
    setUploading(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    
    if (uploadedFiles.length > 0) {
      toast.success(`${uploadedFiles.length} file caricati`);
    }
  };

  const removeFile = (index) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (type) => {
    if (type.startsWith("image/")) return <Image size={20} className="text-blue-400" />;
    if (type.startsWith("video/")) return <Video size={20} className="text-purple-400" />;
    if (type.startsWith("audio/")) return <Mic size={20} className="text-orange-400" />;
    return <Image size={20} />;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.product_name || !formData.product_brand || !formData.purchase_year || !formData.issue_description) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    if (mediaFiles.length === 0) {
      toast.error("È obbligatorio allegare almeno una foto, un video o un audio del problema");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/assistance`, {
        product_name: formData.product_name,
        product_brand: formData.product_brand,
        purchase_year: formData.purchase_year,
        issue_description: formData.issue_description,
        media_files: mediaFiles.map(f => f.url)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(true);
      toast.success("Richiesta di assistenza inviata!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'invio");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6" data-testid="assistenza-success">
        <div className="text-center space-y-6">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
            <CheckCircle className="text-emerald-500" size={48} />
          </div>
          <h1 className="text-2xl font-light text-white">Richiesta Inviata!</h1>
          <p className="text-white/60">
            Il nostro team ti contatterà al più presto per risolvere il problema.
          </p>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate("/profile")}
              className="bg-emerald-500 text-black hover:bg-emerald-400"
            >
              Vedi Richieste
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setSuccess(false);
                setFormData({ product_name: "", product_brand: "", purchase_year: "", issue_description: "" });
                setMediaFiles([]);
              }}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Nuova Richiesta
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="assistenza-page">
      {/* Header */}
      <div className="p-4 space-y-2 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <Wrench className="text-emerald-500" size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-light text-white tracking-wide">Assistenza Prodotto</h1>
            <p className="text-sm text-white/60">Richiedi supporto tecnico</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-4 space-y-6">
        {/* Product Name */}
        <div className="space-y-2">
          <Label className="text-white/70">Su quale prodotto chiedi assistenza? *</Label>
          <Input
            value={formData.product_name}
            onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
            placeholder="es. Finestra scorrevole, Porta blindata, Parquet rovere..."
            className="h-12 bg-zinc-900 border-zinc-800 text-white focus:border-emerald-500"
            required
            data-testid="product-name-input"
          />
        </div>

        {/* Product Brand */}
        <div className="space-y-2">
          <Label className="text-white/70">Di quale azienda/marca è il prodotto? *</Label>
          <Input
            value={formData.product_brand}
            onChange={(e) => setFormData({ ...formData, product_brand: e.target.value })}
            placeholder="es. Schüco, Garofoli, Hörmann..."
            className="h-12 bg-zinc-900 border-zinc-800 text-white focus:border-emerald-500"
            required
            data-testid="product-brand-input"
          />
        </div>

        {/* Purchase Year */}
        <div className="space-y-2">
          <Label className="text-white/70">In che anno è stato acquistato? *</Label>
          <Select value={formData.purchase_year} onValueChange={(v) => setFormData({ ...formData, purchase_year: v })}>
            <SelectTrigger 
              className="h-12 bg-zinc-900 border-zinc-800 text-white"
              data-testid="purchase-year-select"
            >
              <SelectValue placeholder="Seleziona anno" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
              {years.map((year) => (
                <SelectItem key={year} value={year} className="text-white hover:bg-zinc-800">
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Issue Description */}
        <div className="space-y-2">
          <Label className="text-white/70">Qual è il problema riscontrato? *</Label>
          <Textarea
            value={formData.issue_description}
            onChange={(e) => setFormData({ ...formData, issue_description: e.target.value })}
            placeholder="Descrivi nel dettaglio il problema: quando è iniziato, cosa succede, quando si verifica..."
            className="bg-zinc-900 border-zinc-800 text-white min-h-[120px] focus:border-emerald-500"
            required
            data-testid="issue-description-textarea"
          />
        </div>

        {/* Media Files Upload */}
        <div className="space-y-3">
          <Label className="text-white/70">
            Allega foto, video o audio del problema *
          </Label>
          <p className="text-xs text-white/40">
            È obbligatorio allegare almeno un file per aiutarci a capire il problema
          </p>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*,audio/*"
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />
          
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full h-14 border-dashed border-zinc-600 text-white hover:bg-zinc-800"
            data-testid="upload-media-btn"
          >
            {uploading ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : (
              <Upload className="mr-2 h-5 w-5" />
            )}
            {uploading ? "Caricamento..." : "Carica Foto, Video o Audio"}
          </Button>

          {/* Uploaded Files Preview */}
          {mediaFiles.length > 0 && (
            <div className="space-y-2 mt-3">
              <p className="text-xs text-white/60">{mediaFiles.length} file allegati</p>
              <div className="grid grid-cols-2 gap-2">
                {mediaFiles.map((file, index) => (
                  <div 
                    key={index} 
                    className="relative bg-zinc-800 rounded-lg p-3 flex items-center gap-2 group"
                  >
                    {file.type.startsWith("image/") ? (
                      <img 
                        src={file.url} 
                        alt="" 
                        className="w-12 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-zinc-700 rounded flex items-center justify-center">
                        {getFileIcon(file.type)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white truncate">{file.filename}</p>
                      <p className="text-[10px] text-white/40">
                        {file.type.startsWith("image/") ? "Immagine" : 
                         file.type.startsWith("video/") ? "Video" : "Audio"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading || mediaFiles.length === 0}
          className="w-full h-12 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-full disabled:opacity-50"
          data-testid="assistenza-submit"
        >
          {loading ? "Invio in corso..." : "Invia Richiesta Assistenza"}
        </Button>

        {mediaFiles.length === 0 && (
          <p className="text-xs text-center text-red-400">
            * Devi allegare almeno una foto, video o audio per inviare la richiesta
          </p>
        )}
      </form>
    </div>
  );
};

export default AssistenzaPage;
